import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Sparkles, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { topUpWallet } from "@/lib/walletCheckout";

// R217 — Reusable wallet top-up dialog. Pass `open`, `onOpenChange`, and
// optionally `context` { kind, price_paise, balance_paise } from an
// insufficient-balance 402 so the header explains WHY the user is here.
// On success calls `onToppedUp(wallet)` and broadcasts `vk:wallet-updated`.
export function TopUpDialog({ open, onOpenChange, context = null, onToppedUp }) {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState([]);
  const [minTopup, setMinTopup] = useState(4900);
  const [selected, setSelected] = useState(null);     // amount_paise of a tier
  const [custom, setCustom] = useState("");            // rupees string
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/wallet/config").then(({ data }) => {
      setTiers(data.topup_tiers || []);
      setMinTopup(data.min_topup_paise || 4900);
      setCustom("");
      if (context?.price_paise) {
        // Insufficient-balance flow: default to the EXACT amount this action needs.
        setSelected(context.price_paise);
      } else {
        // Generic top-up: preselect the ₹1000 bonus tier (or the first).
        const def = (data.topup_tiers || []).find((x) => x.bonus_pct === 10) || (data.topup_tiers || [])[0];
        if (def) setSelected(def.amount_paise);
      }
    }).catch(() => {});
  }, [open]);

  const exactPaise = context?.price_paise || 0;
  const customPaise = custom.trim() ? Math.round(Number(custom) * 100) : 0;
  const effectiveAmount = customPaise >= minTopup ? customPaise : selected;
  const customInvalid = custom.trim() && customPaise < minTopup;
  const minRupees = Math.round(minTopup / 100);

  const pay = async () => {
    if (!effectiveAmount) return;
    setBusy(true);
    // Close THIS Radix dialog first — its modal overlay/focus-trap would
    // otherwise block clicks inside the Cashfree payment modal.
    onOpenChange?.(false);
    const wallet = await topUpWallet({ amountPaise: effectiveAmount });
    setBusy(false);
    if (wallet) {
      try { window.dispatchEvent(new CustomEvent("vk:wallet-updated")); } catch (_) {}
      onToppedUp?.(wallet);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <DialogContent className="max-w-md" data-testid="wallet-topup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-600" /> {t("wallet.topup_title")}
          </DialogTitle>
        </DialogHeader>

        {context?.price_paise ? (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-slate-700" data-testid="wallet-topup-context">
            {t("wallet.topup_context_cost", { price: Math.round(context.price_paise / 100) })}
            {typeof context.balance_paise === "number" && (
              <> {t("wallet.topup_context_balance", { balance: Math.round(context.balance_paise / 100) })}</>
            )} {t("wallet.topup_context_tail")}
            <div className="mt-1.5 flex items-center gap-1.5 text-[12px] font-bold text-emerald-700" data-testid="wallet-topup-instant-hint">
              <Sparkles className="h-3.5 w-3.5" /> {t("wallet.topup_instant_hint")}
            </div>
            {context?.kind === "custom" && (
              <div className="mt-1.5 text-[12px] font-semibold text-amber-800" data-testid="wallet-topup-bundle-nudge">{t("wallet.topup_bundle_nudge")}</div>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-600 -mt-1">
            {t("wallet.topup_generic")}
          </p>
        )}

        <div className="grid gap-2 mt-1">
          {exactPaise > 0 && (() => {
            const sel = !customInvalid && !customPaise && selected === exactPaise;
            return (
              <button
                type="button"
                data-testid="wallet-exact-amount"
                onClick={() => { setSelected(exactPaise); setCustom(""); }}
                className={"flex items-center justify-between rounded-xl border px-4 py-3 text-left transition " + (
                  sel ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300" : "border-slate-200 hover:bg-slate-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <span className={"grid place-items-center h-5 w-5 rounded-full border shrink-0 " + (sel ? "bg-emerald-600 border-emerald-600" : "border-slate-300")}>
                    {sel ? <Check className="h-3.5 w-3.5 text-white" /> : null}
                  </span>
                  <div className="font-bold text-slate-900">{t("wallet.topup_exact", { amount: Math.round(exactPaise / 100) })}</div>
                </div>
                <span className="text-[11px] font-bold text-white bg-emerald-600 rounded-full px-2 py-0.5">{t("wallet.topup_unlock_tag")}</span>
              </button>
            );
          })()}

          {exactPaise > 0 && tiers.length > 0 && (
            <div className="mt-2 text-[12px] font-semibold text-slate-500" data-testid="wallet-topup-addmore">{t("wallet.topup_addmore")}</div>
          )}

          {tiers.map((tier) => {
            const sel = !customInvalid && !customPaise && selected === tier.amount_paise;
            return (
              <button
                key={tier.amount_paise}
                type="button"
                data-testid={`wallet-tier-${tier.amount_paise}`}
                onClick={() => { setSelected(tier.amount_paise); setCustom(""); }}
                className={"flex items-center justify-between rounded-xl border px-4 py-3 text-left transition " + (
                  sel ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300" : "border-slate-200 hover:bg-slate-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <span className={"grid place-items-center h-5 w-5 rounded-full border shrink-0 " + (sel ? "bg-emerald-600 border-emerald-600" : "border-slate-300")}>
                    {sel ? <Check className="h-3.5 w-3.5 text-white" /> : null}
                  </span>
                  <div>
                    <div className="font-bold text-slate-900">{t("wallet.tier_pay", { amount: tier.amount_display })}</div>
                    {tier.bonus_pct ? (
                      <div className="text-[12px] text-emerald-700 font-semibold flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> {t("wallet.tier_bonus", { pct: tier.bonus_pct, credit: tier.credit_display })}
                      </div>
                    ) : (
                      <div className="text-[12px] text-slate-500">{t("wallet.tier_credit", { credit: tier.credit_display })}</div>
                    )}
                    {context?.kind === "custom" && tier.credit_paise ? (
                      <div className="text-[11px] text-slate-500" data-testid={`wallet-tier-ideas-${tier.amount_paise}`}>{t("wallet.topup_ideas_hint", { n: Math.floor(tier.credit_paise / 4900) })}</div>
                    ) : null}
                  </div>
                </div>
                {tier.bonus_pct && tier.amount_paise === 199900 ? (
                  <span className="text-[11px] font-bold text-white bg-emerald-600 rounded-full px-2 py-0.5">{t("wallet.best_value")}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="mt-1">
          <label className="text-xs font-semibold text-slate-600">{t("wallet.custom_label")}</label>
          <Input
            type="number"
            inputMode="numeric"
            value={custom}
            min={minRupees}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={t("wallet.custom_min_ph", { min: minRupees })}
            className="mt-1 h-10"
            data-testid="wallet-custom-amount"
          />
          {customInvalid && (
            <div className="text-[12px] text-rose-600 mt-1">{t("wallet.custom_min_err", { min: minRupees })}</div>
          )}
        </div>

        <Button
          onClick={pay}
          disabled={busy || !effectiveAmount || customInvalid}
          data-testid="wallet-topup-pay"
          className="w-full h-11 mt-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
        >
          {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("wallet.pay_processing")}</>
                : effectiveAmount ? t("wallet.pay_secure", { amount: Math.round(effectiveAmount / 100).toLocaleString("en-IN") }) : t("wallet.pay_choose")}
        </Button>
        <p className="text-[11px] text-slate-400 text-center">{t("wallet.secured_note")}</p>
      </DialogContent>
    </Dialog>
  );
}
